import { createContext, useState, useContext } from 'react'
import { useConfig } from './ConfigProvider'
import { ChangeCustOrderNumber, GetOrderItems, GetOrdersFromView, GetOrderFiltersData, GetWzDocFromView, GetWzDocFiltersData } from '../Services/OrderService'
import { toast } from 'react-toastify'
import { useAuth } from './AuthProvider'
import axios from 'axios'


export const OrderContext = createContext()
export const useOrder = () => {
    return useContext(OrderContext)
}

export const OrderProvider = ({ children }) => {
    const { api } = useConfig()
    const [orders, setOrders] = useState([])

    const [ordersHasMore, setOrdersHasMore] = useState(false)
    const [isLoadingOrders, setIsLoadingOrders] = useState(false)
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null)
    const { logout } = useAuth()
    const [wzDocHasMore, setWzDocHasMore] = useState(false)
    const [wzDocs, setWzDocs] = useState([])
    const [isLoadingWzDoc, setIsLoadingWzDoc] = useState(false)

    const [orderInfo, setOrderInfo] = useState({
        authorizedPersons: null,
        deliveryDate: null,
        deliveryType: null,
        deliveryAddress: null,
        payment: null,
        comment: null,
        expectedDeliveryTime: null,
        isCustomDeliveryTime: false,
        attachments: [],
    })

    const clearOrderInfo = () => {
        setOrderInfo(prev => ({
            authorizedPersons: null,
            deliveryDate: null,
            deliveryType: null,
            deliveryAddress: null,
            payment: null,
            comment: null,
            expectedDeliveryTime: null,
            isCustomDeliveryTime: false,
            attachments: prev.attachments,
        }))
    }

    const changeCustOrderNumber = async (docNum, custDocNum, custId, siteId) => {
        try {
            const res = await ChangeCustOrderNumber(api, docNum, custDocNum, custId, siteId)
            return true
        } catch (e) {
            console.log(e)
            if (e.response.status === 403) {
                if (e.response.data.includes('Brak uprawnień')) {
                    toast.error('Brak uprawnień!')
                }
                else { //konto niekatywne, automatyczne wylogowanie
                    logout(false, false)
                }
            }
            return false
        }
    }

    const handleGetOrders = (ordersFilterCriteria, pageNo, cancelToken) => {
        if (pageNo === 0) {
            setOrders([])
        }
        setIsLoadingOrders(true)
        GetOrdersFromView(api, ordersFilterCriteria, cancelToken, pageNo)
            .then(res => {
                const newOrders = res.data
                setOrders(prev => {
                    const existingOrderNumbers = new Set(prev.map(order => order.ordersNumber))
                    const uniqueNewOrders = newOrders.filter(order => !existingOrderNumbers.has(order.ordersNumber))
                    return [...prev, ...uniqueNewOrders]
                })
                setOrdersHasMore(newOrders.length > 0)
            })
            .catch(e => {
                if (axios.isCancel(e)) {
                    return
                }
                console.log(e)
                if (e.response?.status === 403) {
                    if (e.response.data?.includes('Brak uprawnień')) {
                        toast.error('Brak uprawnień!')
                    } else {
                        logout(false, false) // konto nieaktywne
                    }
                }
                setOrders([])
            })
            .finally(() => {
                setIsLoadingOrders(false)
            })
    }

    const handleGetWzDoc = (wzFilterCriteria, pageNo, cancelToken) => {
        if (pageNo === 0) {
            setWzDocs([])
        }
        setIsLoadingWzDoc(true)
        GetWzDocFromView(api, wzFilterCriteria, cancelToken, pageNo)
            .then(res => {
                const newWZDocs = res.data
                setWzDocs(prev => {
                    const existingOrderNumbers = new Set(prev.map(order => order.wzDocNumber))
                    const uniqueNewOrders = newWZDocs.filter(order => !existingOrderNumbers.has(order.wzDocNumber))
                    return [...prev, ...uniqueNewOrders]
                })
                setWzDocHasMore(newWZDocs.length > 0)
            })
            .catch(e => {
                if (axios.isCancel(e)) {
                    return
                }
                console.log(e)
                if (e.response?.status === 403) {
                    if (e.response.data?.includes('Brak uprawnień')) {
                        toast.error('Brak uprawnień!')
                    } else {
                        logout(false, false) // konto nieaktywne
                    }
                }
                setWzDocs([])
            })
            .finally(() => {
                setIsLoadingWzDoc(false)
            })

    }


    const handleGetOrderItems = async (orderNumber) => {
        try {
            const result = await GetOrderItems(api, orderNumber)
            return result.data
        } catch (e) {
            console.log(e)
            if (e.response.status === 403) {
                if (e.response.data.includes('Brak uprawnień')) {
                    toast.error('Brak uprawnień!')
                }
                else { //konto niekatywne, automatyczne wylogowanie
                    logout(false, false)
                }
            }
            return []
        }
    }

    const handleGetOrderFiltersData = async (filters, cancelToken) => {
        try {
            const res = await GetOrderFiltersData(api, filters, cancelToken)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const handleGetWzFiltersData = async (filters) => {
        try {
            const res = await GetWzDocFiltersData(api, filters)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }

    return (
        <OrderContext.Provider value={{
            orderInfo,
            selectedOrderDetails,
            orders,
            wzDocs,
            isLoadingWzDoc,
            wzDocHasMore,
            isLoadingOrders,
            ordersHasMore,
            setOrderInfo,
            clearOrderInfo,
            changeCustOrderNumber,
            setSelectedOrderDetails,
            handleGetOrders,
            handleGetOrderItems,
            handleGetOrderFiltersData,
            handleGetWzFiltersData,
            setOrders,
            handleGetWzDoc,
            setWzDocs,
        }}>
            {children}
        </OrderContext.Provider>
    )
}